// ProblemAnswer 컴포넌트에서 사용하는 유틸리티 함수들

// 원문자(①~⑳)에서 숫자 추출 (U+2460~U+2473)
const getCircledNumber = (s: string): number | null => {
  if (s.length !== 1) return null;
  const code = s.charCodeAt(0);
  if (code >= 0x2460 && code <= 0x2473) return code - 0x245F;
  return null;
};

// 객체 키를 정렬하는 함수 (순서 보장)
export const getSortedKeys = (obj: Record<string, string | number | string[]>): string[] => {
  return Object.keys(obj).sort((a, b) => {
    // "(1)", "(2)" 형태의 키를 숫자 순서로 정렬
    const aMatch = a.match(/\((\d+)\)/);
    const bMatch = b.match(/\((\d+)\)/);

    if (aMatch && bMatch) {
      return parseInt(aMatch[1]) - parseInt(bMatch[1]);
    }

    // 원문자(①②③...) 키를 숫자 순서로 정렬
    const aCircled = getCircledNumber(a);
    const bCircled = getCircledNumber(b);
    if (aCircled !== null && bCircled !== null) {
      return aCircled - bCircled;
    }

    // 일반 숫자 키 정렬
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }

    // 그 외는 문자열 정렬
    return a.localeCompare(b);
  });
};

// 객체를 정렬된 키 순서로 entries 반환
export const getSortedEntries = (
  obj: Record<string, string | number | string[]>
): [string, string | number | string[]][] => {
  return getSortedKeys(obj).map((key) => [key, obj[key]]);
};

// correctAnswer 타입 정의
export type CorrectAnswerType =
  | string
  | string[]
  | Record<string, string | number | string[]>
  | Record<string, string | number>[];

// 답안이 객체인지 확인하는 함수
export const isObjectAnswer = (
  answer: CorrectAnswerType
): answer is Record<string, string | number | string[]> => {
  return typeof answer === "object" && answer !== null && !Array.isArray(answer);
};

// 답안이 연결된 답 세트 배열인지 확인하는 함수
// 예: [{ "(a)": "%", "(b)": "10" }, { "(a)": "%", "(b)": "2" }]
export const isAnswerSets = (
  answer: CorrectAnswerType
): answer is Record<string, string | number>[] => {
  return (
    Array.isArray(answer) &&
    answer.length > 0 &&
    typeof answer[0] === "object" &&
    answer[0] !== null
  );
};

// hiddenCorrectAnswers 타입 정의
// - string[]: 단일 정답의 숨은 변형
// - Record<string, string[]>: 칸별 숨은 변형 (object correctAnswer용)
// - Record<string, string | number>[]: 숨은 답 세트 (object correctAnswer용).
//   칸별 변형으로는 행이 섞인 답까지 정답이 되는 결과표에서, 화면에는 대표 세트만
//   보여 주고 다른 행 순서를 세트 단위로 인정할 때 쓴다.
export type HiddenCorrectAnswersType =
  | string[]
  | Record<string, string[]>
  | Record<string, string | number>[];

// correctAnswer와 hiddenCorrectAnswers를 합쳐 채점용 답안을 만드는 함수
export const mergeHiddenCorrectAnswers = (
  correctAnswer: CorrectAnswerType,
  hiddenCorrectAnswers?: HiddenCorrectAnswersType
): CorrectAnswerType => {
  if (!hiddenCorrectAnswers) return correctAnswer;

  if (Array.isArray(hiddenCorrectAnswers)) {
    if (hiddenCorrectAnswers.length === 0) return correctAnswer;

    // 숨은 답 세트: 대표 답(Record 또는 답 세트 배열) 뒤에 세트 단위로 이어 붙인다
    if (isAnswerSets(hiddenCorrectAnswers as CorrectAnswerType)) {
      const hiddenSets = hiddenCorrectAnswers as Record<string, string | number>[];
      if (isAnswerSets(correctAnswer)) return [...correctAnswer, ...hiddenSets];
      if (isObjectAnswer(correctAnswer)) {
        return [correctAnswer as Record<string, string | number>, ...hiddenSets];
      }
      return correctAnswer;
    }

    // string[] 형태의 hiddenCorrectAnswers
    const hiddenValues = hiddenCorrectAnswers as string[];
    if (typeof correctAnswer === "string") {
      return [correctAnswer, ...hiddenValues];
    }
    if (Array.isArray(correctAnswer) && correctAnswer.every((a) => typeof a === "string")) {
      return [...(correctAnswer as string[]), ...hiddenValues];
    }
    return correctAnswer;
  }

  // Record<string, string[]> 형태의 hiddenCorrectAnswers (object correctAnswer용)
  if (isObjectAnswer(correctAnswer)) {
    const merged = { ...correctAnswer } as Record<string, string | number | string[]>;
    for (const [key, hiddenValues] of Object.entries(hiddenCorrectAnswers)) {
      const existing = merged[key];
      if (Array.isArray(existing)) {
        merged[key] = [...existing, ...hiddenValues];
      } else if (typeof existing === "string") {
        merged[key] = [existing, ...hiddenValues];
      }
    }
    return merged;
  }

  return correctAnswer;
};

// 답 세트에서 가장 일치하는 세트를 찾는 함수
export const findBestMatchingSet = (
  userAnswer: Record<string, string | number>,
  answerSets: Record<string, string | number>[],
  strictWhitespace = false,
  caseSensitive = false,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
): Record<string, string | number> => {
  const norm = (t: string) =>
    normalizeForComparison(
      t,
      strictWhitespace,
      caseSensitive,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );

  let bestSet = answerSets[0];
  let bestMatches = -1;

  for (const set of answerSets) {
    let matches = 0;
    for (const key of Object.keys(set)) {
      const userValue = norm((userAnswer[key] || "").toString());
      const expandedAnswers = generateQuoteVariations(set[key].toString()).map(norm);
      if (expandedAnswers.includes(userValue)) matches++;
    }
    if (matches > bestMatches) {
      bestMatches = matches;
      bestSet = set;
    }
  }

  return bestSet;
};

// JSX 속성을 파싱하는 함수
export const parseJSXProps = (
  jsxString: string
): Record<string, string | number | boolean> => {
  const props: Record<string, string | number | boolean> = {};

  // JSX 문자열에서 속성들을 추출
  // 속성 패턴: prop="value", prop='value', prop={value}, prop (boolean)
  const propRegex = /(\w+)(?:=(?:"([^"]*)"|'([^']*)'|\{([^}]*)\}))?/g;
  let match;

  while ((match = propRegex.exec(jsxString)) !== null) {
    const [, propName, doubleQuotedValue, singleQuotedValue, bracedValue] =
      match;

    if (doubleQuotedValue !== undefined) {
      // 문자열 값 (double quote)
      props[propName] = doubleQuotedValue;
    } else if (singleQuotedValue !== undefined) {
      // 문자열 값 (single quote)
      props[propName] = singleQuotedValue;
    } else if (bracedValue !== undefined) {
      // 중괄호 안의 값 (숫자, boolean, expression)
      const trimmedValue = bracedValue.trim();
      if (trimmedValue === "true") {
        props[propName] = true;
      } else if (trimmedValue === "false") {
        props[propName] = false;
      } else if (!isNaN(Number(trimmedValue))) {
        props[propName] = Number(trimmedValue);
      } else {
        // 다른 expression의 경우 문자열로 저장
        props[propName] = trimmedValue;
      }
    } else {
      // 속성명만 있는 경우 (boolean true)
      props[propName] = true;
    }
  }

  return props;
};

// findContentBlocks는 contentBlocks.ts로 이동됨
export { findContentBlocks } from "./contentBlocks";

// MDX 표를 HTML 표로 변환하는 함수
export const convertMdxTableToHtml = (text: string) => {
  const lines = text.trim().split("\n");
  if (lines.length < 3) return null;

  // 헤더 파싱
  const headerLine = lines[0].trim();
  const headers = headerLine
    .slice(1, -1) // 앞뒤 | 제거
    .split("|")
    .map((cell) => cell.trim());

  // 구분선 파싱 (정렬 정보 추출)
  const separatorLine = lines[1].trim();
  const alignments = separatorLine
    .slice(1, -1) // 앞뒤 | 제거
    .split("|")
    .map((cell) => {
      const trimmed = cell.trim();
      if (trimmed.startsWith(":") && trimmed.endsWith(":")) return "center";
      if (trimmed.endsWith(":")) return "right";
      return "left";
    });

  // 데이터 행 파싱
  const dataRows = lines
    .slice(2)
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) return null;
      return trimmed
        .slice(1, -1) // 앞뒤 | 제거
        .split("|")
        .map((cell) => cell.trim());
    })
    .filter((row) => row !== null);

  return {
    headers,
    alignments,
    dataRows,
  };
};

// 답안 비교를 위한 정규화 함수
// 홑따옴표, 화살표 등 특수 문자 변환 + 공백 제거 + 소문자 변환
// strictWhitespace가 true이면 줄바꿈과 띄어쓰기를 보존 (코드 출력 결과 문제용)
// caseSensitive가 true이면 대소문자를 구분 (Python의 True/False/None 등)
// ignoreTrailingSemicolon이 true이면 문장 끝 세미콜론 1개를 무시 (SQL 채점용)
// allowQuotedSqlAlias가 true이면 SQL 문자열 리터럴 밖의 AS "단순별칭"을 AS 별칭으로 정규화
const SIMPLE_QUOTED_SQL_ALIAS =
  /\b(AS)(\s+)(?:"([\p{L}_][\p{L}\p{N}_$]*)"|“([\p{L}_][\p{L}\p{N}_$]*)”)(?![\p{L}\p{N}_$])/giu;

const normalizeQuotedSqlAliases = (text: string): string => {
  let result = "";
  let segmentStart = 0;
  let inSingleQuotedLiteral = false;

  // SQL 데이터 안의 `AS "..."`는 별칭 문법이 아니므로 single-quoted literal은 건드리지 않는다.
  const normalizeSegment = (segment: string) =>
    segment.replace(
      SIMPLE_QUOTED_SQL_ALIAS,
      (
        _match,
        keyword: string,
        whitespace: string,
        asciiAlias: string | undefined,
        smartAlias: string | undefined
      ) =>
        `${keyword}${whitespace}${asciiAlias ?? smartAlias}`
    );

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== "'") continue;

    if (inSingleQuotedLiteral && text[index + 1] === "'") {
      index += 1;
      continue;
    }

    if (!inSingleQuotedLiteral) {
      result += normalizeSegment(text.slice(segmentStart, index));
      segmentStart = index;
      inSingleQuotedLiteral = true;
      continue;
    }

    result += text.slice(segmentStart, index + 1);
    segmentStart = index + 1;
    inSingleQuotedLiteral = false;
  }

  const remainder = text.slice(segmentStart);
  return (
    result + (inSingleQuotedLiteral ? remainder : normalizeSegment(remainder))
  );
};

export const normalizeForComparison = (
  text: string | number | null | undefined,
  strictWhitespace = false,
  caseSensitive = false,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
): string => {
  if (text === null || text === undefined || text === "") return "";

  // 호출부 일부가 `as string` 캐스팅으로만 처리하여 number 등이 흘러들어올 수 있음.
  // text.replace 호출 전 안전하게 문자열로 강제 변환.
  const str = typeof text === "string" ? text : String(text);

  const normalizedQuotes = str
    .replace(/\\n/g, "\n") // 리터럴 "\n" (백슬래시+n)을 실제 줄바꿈으로 변환 (JSX 속성 문자열 대응)
    .replace(/\\t/g, "\t") // 리터럴 "\t"도 동일하게 처리
    .replace(/[\u0027\u2018\u2019]/g, "\u0027") // 모든 홑따옴표를 일반 홑따옴표로 정규화
    .replace(/→/g, "->"); // 유니코드 화살표를 ASCII 화살표로 정규화

  const normalized = allowQuotedSqlAlias
    ? normalizeQuotedSqlAliases(normalizedQuotes)
    : normalizedQuotes;

  const comparable = ignoreTrailingSemicolon
    ? normalized.replace(/;\s*$/, "")
    : normalized;

  if (strictWhitespace) {
    // 줄바꿈·띄어쓰기 보존: 각 줄의 앞뒤 공백만 제거
    const lines = comparable
      .split("\n")
      .map((line) => caseSensitive ? line.trim() : line.trim().toLowerCase());

    // 앞뒤 빈 줄 제거
    while (lines.length > 0 && lines[0] === "") {
      lines.shift();
    }
    while (lines.length > 0 && lines[lines.length - 1] === "") {
      lines.pop();
    }

    return lines.join("\n");
  }

  // 기본: 모든 공백 제거
  const stripped = comparable.replace(/\s+/g, "");
  return caseSensitive ? stripped : stripped.toLowerCase();
};

// 홑따옴표 변형을 자동으로 생성하는 함수
export const generateQuoteVariations = (answer: string): string[] => {
  if (!answer) return [];
  const variations = new Set<string>();

  // 원본 답안 추가
  variations.add(answer);

  // 모든 홑따옴표를 일반 홑따옴표로 변환 (ASCII 39: ')
  const normalQuotes = answer.replace(/[\u0027\u2018\u2019]/g, "\u0027");
  variations.add(normalQuotes);

  // 모든 홑따옴표를 왼쪽 스마트 홑따옴표로 변환 (U+2018: ')
  const smartQuotesLeft = answer.replace(/[\u0027\u2018\u2019]/g, "\u2018");
  variations.add(smartQuotesLeft);

  // 모든 홑따옴표를 오른쪽 스마트 홑따옴표로 변환 (U+2019: ')
  const smartQuotesRight = answer.replace(/[\u0027\u2018\u2019]/g, "\u2019");
  variations.add(smartQuotesRight);

  return Array.from(variations);
};

// 답안 배열을 확장하는 함수 (홑따옴표 변형 포함)
export const expandAnswerArray = (answers: string[]): string[] => {
  if (!answers) return [];
  const expandedAnswers = new Set<string>();

  answers.filter(Boolean).forEach(answer => {
    const variations = generateQuoteVariations(answer);
    variations.forEach(variation => {
      expandedAnswers.add(variation);
    });
  });

  return Array.from(expandedAnswers);
};

// 객체의 배열 값들을 평탄화하는 함수
export const flattenObjectValues = (obj: Record<string, string | number | string[]>): string[] => {
  const flattenedValues: string[] = [];

  Object.values(obj).forEach(value => {
    if (Array.isArray(value)) {
      // 배열인 경우 모든 요소를 추가
      flattenedValues.push(...value);
    } else {
      // 단일 값인 경우 그대로 추가
      flattenedValues.push(value.toString());
    }
  });

  return flattenedValues;
};

type ParsedSetElement = {
  value: string;
  quoteType: "single" | "double" | null;
};

const SINGLE_QUOTE_CHARS = "'\u2018\u2019";
const DOUBLE_QUOTE_CHARS = "\"\u201C\u201D";

const isWrappedWith = (value: string, quoteChars: string): boolean => {
  if (value.length < 2) return false;
  return (
    quoteChars.includes(value[0]) &&
    quoteChars.includes(value[value.length - 1])
  );
};

const getOuterQuoteType = (value: string): ParsedSetElement["quoteType"] => {
  if (isWrappedWith(value, SINGLE_QUOTE_CHARS)) return "single";
  if (isWrappedWith(value, DOUBLE_QUOTE_CHARS)) return "double";
  return null;
};

const stripOuterQuotes = (value: string): string => {
  if (getOuterQuoteType(value)) {
    return value.slice(1, -1);
  }
  return value;
};

const getSetElementKey = (element: ParsedSetElement): string =>
  `${element.quoteType ?? "none"}:${element.value}`;

// Python set 형태의 문자열에서 원소를 추출하여 집합으로 비교하는 함수
// 예: "{'한국', '중국', '베트남'}" → 원소 값 + 바깥 따옴표 종류 목록
export const compareAsSetElements = (
  userAnswer: string,
  correctAnswer: string,
  caseSensitive = false
): boolean => {
  const parseSetElements = (str: string): ParsedSetElement[] | null => {
    const trimmed = str.trim();
    // {로 시작하고 }로 끝나는지 확인
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
    const inner = trimmed.slice(1, -1);
    if (inner.trim() === "") return [];
    const elements = inner.split(",").map((el) => {
      const cleaned = el.trim();
      const unquoted = stripOuterQuotes(cleaned).trim();
      return {
        value: caseSensitive ? unquoted : unquoted.toLowerCase(),
        quoteType: getOuterQuoteType(cleaned),
      };
    });
    const elementSet = new Set(elements.map(getSetElementKey));
    // 중복 원소가 있으면 실제 Python set 출력과 다르므로 오답 처리
    if (elementSet.size !== elements.length) return null;
    return elements;
  };

  const userSet = parseSetElements(userAnswer);
  const correctSet = parseSetElements(correctAnswer);

  if (!userSet || !correctSet) return false;
  if (userSet.length !== correctSet.length) return false;

  const correctElementsByValue = new Map(
    correctSet.map((element) => [getSetElementKey(element), element])
  );

  for (const elem of userSet) {
    const correctElement = correctElementsByValue.get(getSetElementKey(elem));
    if (!correctElement) return false;
  }
  return true;
};

// ignoreOrder=true일 때 사용자 입력 키와 정답 그룹 간 최대 이분 매칭을 계산
// - 각 사용자 입력은 최대 1개의 정답 그룹에만 매칭됨 (유일성 제약)
// - 매칭된 사용자 키 집합을 반환 (매칭 실패한 키는 미포함)
// - 같은 그룹에 복수 입력이 매칭 가능한 경우 augmenting path 알고리즘으로 최대 매칭 탐색
// - primaryAnswer가 주어지면 primary 답에 매칭되는 입력을 먼저 처리하여 primary가 파란색으로 표시되도록 보장
export const computeIgnoreOrderMatchedKeys = (
  userAnswer: Record<string, string | number>,
  correctAnswer: Record<string, string | number | string[]>,
  strictWhitespace = false,
  caseSensitive = false,
  primaryAnswer?: Record<string, string | number | string[]>,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
): Set<string> => {
  const norm = (t: string) =>
    normalizeForComparison(
      t,
      strictWhitespace,
      caseSensitive,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );

  const answerGroups = Object.values(correctAnswer).map((value) =>
    Array.isArray(value)
      ? value.map((v) => norm(v.toString()))
      : [norm(value.toString())]
  );

  // primary 답 값들의 플랫 집합 (매칭 우선순위 결정용)
  const primaryValueSet = primaryAnswer
    ? new Set(
        Object.values(primaryAnswer).flatMap((value) =>
          Array.isArray(value)
            ? value.map((v) => norm(v.toString()))
            : [norm(value.toString())]
        )
      )
    : null;

  const rawUserEntries = Object.entries(userAnswer)
    .map(([k, v]) => [k, norm((v ?? "").toString())] as const)
    .filter(([, v]) => v !== "");

  // primary-first 휴리스틱: primary 값에 매칭되는 입력을 먼저 처리
  // Why: "복합체"(primary) + "컴포지트"(hidden)가 같은 그룹에 경쟁할 때 primary인 "복합체"가
  // 파란색으로 남아야 사용자가 자기 답이 맞았는지 직관적으로 판단 가능
  const userEntries = primaryValueSet
    ? [...rawUserEntries].sort(([, a], [, b]) => {
        const aPrimary = primaryValueSet.has(a) ? 0 : 1;
        const bPrimary = primaryValueSet.has(b) ? 0 : 1;
        return aPrimary - bPrimary;
      })
    : rawUserEntries;

  const matchedGroup = new Map<string, number>();
  const groupToUser = new Map<number, string>();

  const tryMatch = (
    userKey: string,
    userValue: string,
    visited: Set<number>
  ): boolean => {
    for (let g = 0; g < answerGroups.length; g++) {
      if (!answerGroups[g].includes(userValue)) continue;
      if (visited.has(g)) continue;
      visited.add(g);
      const owner = groupToUser.get(g);
      if (owner === undefined) {
        matchedGroup.set(userKey, g);
        groupToUser.set(g, userKey);
        return true;
      }
      const ownerValue = norm((userAnswer[owner] ?? "").toString());
      if (tryMatch(owner, ownerValue, visited)) {
        matchedGroup.set(userKey, g);
        groupToUser.set(g, userKey);
        return true;
      }
    }
    return false;
  };

  for (const [key, value] of userEntries) {
    tryMatch(key, value, new Set());
  }

  return new Set(matchedGroup.keys());
};

// 정답 검증 함수들
export const getItemCorrectness = (
  key: string,
  value: string | number | string[],
  userAnswer: Record<string, string | number>,
  correctAnswer: Record<string, string | number | string[]>,
  ignoreOrder: boolean,
  strictWhitespace = false,
  caseSensitive = false,
  primaryAnswer?: Record<string, string | number | string[]>,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
) => {
  const norm = (t: string) =>
    normalizeForComparison(
      t,
      strictWhitespace,
      caseSensitive,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );
  const userValue = norm((userAnswer[key] || "").toString());

  if (ignoreOrder) {
    // 순서 무시: 이분 매칭으로 그룹 유일성을 보장한 후 해당 키가 매칭되었는지 확인
    // Why: flattenObjectValues로 단순 포함 검사만 하면, 같은 그룹에 속하는 입력 두 개가
    // 모두 파란색(정답)으로 표시되는 버그 발생 (JCG-1713). 예: 구조 다이어그램 문제에서
    // "컴포지트"(Composite 그룹의 hidden answer)와 "복합체"(Composite 그룹의 정답)가
    // 둘 다 정답으로 표시되는 문제. primaryAnswer를 넘기면 primary 답이 우선 매칭되어
    // "복합체"가 파란색으로 남고 "컴포지트"가 빨간색으로 표시됨.
    if (userValue === "") return false;
    const matched = computeIgnoreOrderMatchedKeys(
      userAnswer,
      correctAnswer,
      strictWhitespace,
      caseSensitive,
      primaryAnswer,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );
    return matched.has(key);
  } else {
    // 기존 방식: 해당 키의 정답과 일치하는지 확인
    if (Array.isArray(value)) {
      // 배열인 경우: 홑따옴표 변형을 포함한 확장된 답안 배열 생성
      const expandedAnswers = expandAnswerArray(value);
      const normalizedExpandedAnswers = expandedAnswers.map(norm);
      return normalizedExpandedAnswers.includes(userValue);
    } else {
      // 단일 값인 경우: 홑따옴표 변형을 포함한 확장된 답안 배열 생성
      const expandedAnswers = generateQuoteVariations(value.toString());
      const normalizedExpandedAnswers = expandedAnswers.map(norm);
      return normalizedExpandedAnswers.includes(userValue);
    }
  }
};

export const checkAnswerCorrectness = (
  userAnswer: string | Record<string, string | number>,
  correctAnswer: CorrectAnswerType,
  ignoreOrder: boolean,
  strictWhitespace = false,
  caseSensitive = false,
  compareAsSet = false,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
): boolean => {
  if (!correctAnswer) return false;

  const norm = (t: string) =>
    normalizeForComparison(
      t,
      strictWhitespace,
      caseSensitive,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );

  // compareAsSet: Python set 등 순서 없는 집합 출력 비교
  if (compareAsSet && typeof correctAnswer === "string" && typeof userAnswer === "string") {
    return compareAsSetElements(userAnswer, correctAnswer, caseSensitive);
  }

  // 답 세트 배열 처리: 사용자 답이 어느 세트와든 완전히 일치하면 정답
  if (isAnswerSets(correctAnswer)) {
    const answers = userAnswer as Record<string, string | number>;
    return correctAnswer.some((answerSet) =>
      Object.keys(answerSet).every((key) => {
        const userValue = norm((answers[key] || "").toString());
        const expandedAnswers = generateQuoteVariations(
          answerSet[key].toString()
        ).map(norm);
        return expandedAnswers.includes(userValue);
      })
    );
  }

  if (isObjectAnswer(correctAnswer)) {
    const answers = userAnswer as Record<string, string | number>;

    if (ignoreOrder) {
      // 순서 무시: 각 정답 그룹과 1:1 매칭되어야 함

      // 1. correctAnswer를 정답 그룹 배열로 변환
      const answerGroups = Object.values(correctAnswer).map(value =>
        Array.isArray(value)
          ? value.map(v => norm(v.toString()))
          : [norm(value.toString())]
      );

      // 2. 사용자 입력값 정규화
      const userValues = Object.values(answers)
        .map(val => norm(val.toString()))
        .filter(val => val !== "");

      // 3. 개수 확인
      if (userValues.length !== answerGroups.length) {
        return false;
      }

      // 4. 각 사용자 입력이 서로 다른 그룹과 매칭되는지 확인
      const usedGroupIndices = new Set<number>();

      for (const userValue of userValues) {
        const matchingGroupIndex = answerGroups.findIndex((group, index) =>
          !usedGroupIndices.has(index) && group.includes(userValue)
        );

        if (matchingGroupIndex === -1) {
          return false; // 매칭되는 그룹이 없거나 이미 사용됨
        }

        usedGroupIndices.add(matchingGroupIndex);
      }

      return usedGroupIndices.size === answerGroups.length;
    } else {
      // 기존 방식: 키별로 정확한 값이 입력되어야 함
      return getSortedKeys(correctAnswer).every((key) => {
        const userValue = norm((answers[key] || "").toString());
        const correctValue = correctAnswer[key];

        if (Array.isArray(correctValue)) {
          // 배열인 경우: 홑따옴표 변형을 포함한 확장된 답안 배열 생성
          const expandedAnswers = expandAnswerArray(correctValue);
          const normalizedExpandedAnswers = expandedAnswers.map(norm);
          return normalizedExpandedAnswers.includes(userValue);
        } else {
          // 단일 값인 경우: 홑따옴표 변형을 포함한 확장된 답안 배열 생성
          const expandedAnswers = generateQuoteVariations(correctValue.toString());
          const normalizedExpandedAnswers = expandedAnswers.map(norm);
          return normalizedExpandedAnswers.includes(userValue);
        }
      });
    }
  } else if (Array.isArray(correctAnswer)) {
    // 배열 답안 처리
    // userAnswer가 객체 모드(Record)인데 분기가 어긋나 들어오는 경우 빈답으로 처리 (런타임 크래시 방지)
    const normalizedUserAnswer = norm(typeof userAnswer === "string" ? userAnswer : "");

    // 사용자가 아무것도 입력하지 않았으면 false 반환
    if (normalizedUserAnswer === "") {
      return false;
    }

    // 홑따옴표 변형을 포함한 확장된 답안 배열 생성
    const expandedAnswers = expandAnswerArray(correctAnswer);

    // 사용자 답안이 확장된 답안 중 하나와 일치하는지 확인
    return expandedAnswers.some(answer =>
      norm(answer) === normalizedUserAnswer
    );
  } else {
    // 단일 문자열 답안 처리
    const normalizedUserAnswer = norm(typeof userAnswer === "string" ? userAnswer : "");

    // 사용자가 아무것도 입력하지 않았으면 false 반환
    if (normalizedUserAnswer === "") {
      return false;
    }

    // 홑따옴표 변형을 포함한 확장된 답안 배열 생성
    const expandedAnswers = generateQuoteVariations(correctAnswer);

    // 사용자 답안이 확장된 답안 중 하나와 일치하는지 확인
    return expandedAnswers.some(answer =>
      norm(answer) === normalizedUserAnswer
    );
  }
};

export interface AnswerScore {
  correctItems: number;
  totalItems: number;
  hasAnyAnswer: boolean;
  isComplete: boolean;
}

const hasAnswerValue = (value: string | number | undefined): boolean =>
  value !== undefined && value.toString().trim() !== "";

export const getAnswerScore = (
  userAnswer: string | Record<string, string | number>,
  correctAnswer: CorrectAnswerType,
  ignoreOrder: boolean,
  strictWhitespace = false,
  caseSensitive = false,
  compareAsSet = false,
  ignoreTrailingSemicolon = false,
  allowQuotedSqlAlias = false
): AnswerScore => {
  if (!correctAnswer) {
    return {
      correctItems: 0,
      totalItems: 0,
      hasAnyAnswer: false,
      isComplete: false,
    };
  }

  if (isAnswerSets(correctAnswer)) {
    const keys = getSortedKeys(correctAnswer[0]);
    const answers =
      typeof userAnswer === "object" && !Array.isArray(userAnswer)
        ? userAnswer
        : {};
    const bestSet = findBestMatchingSet(
      answers,
      correctAnswer,
      strictWhitespace,
      caseSensitive,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    );
    const correctItems = keys.filter((key) =>
      getItemCorrectness(
        key,
        bestSet[key],
        answers,
        bestSet,
        false,
        strictWhitespace,
        caseSensitive,
        undefined,
        ignoreTrailingSemicolon,
        allowQuotedSqlAlias
      )
    ).length;

    return {
      correctItems,
      totalItems: keys.length,
      hasAnyAnswer: keys.some((key) => hasAnswerValue(answers[key])),
      isComplete: keys.every((key) => hasAnswerValue(answers[key])),
    };
  }

  if (isObjectAnswer(correctAnswer)) {
    const keys = getSortedKeys(correctAnswer);
    const answers =
      typeof userAnswer === "object" && !Array.isArray(userAnswer)
        ? userAnswer
        : {};
    const correctItems = keys.filter((key) =>
      getItemCorrectness(
        key,
        correctAnswer[key],
        answers,
        correctAnswer,
        ignoreOrder,
        strictWhitespace,
        caseSensitive,
        undefined,
        ignoreTrailingSemicolon,
        allowQuotedSqlAlias
      )
    ).length;

    return {
      correctItems,
      totalItems: keys.length,
      hasAnyAnswer: keys.some((key) => hasAnswerValue(answers[key])),
      isComplete: keys.every((key) => hasAnswerValue(answers[key])),
    };
  }

  const hasAnyAnswer =
    typeof userAnswer === "string" && userAnswer.trim() !== "";

  return {
    correctItems:
      hasAnyAnswer &&
      checkAnswerCorrectness(
        userAnswer,
        correctAnswer,
        ignoreOrder,
        strictWhitespace,
        caseSensitive,
        compareAsSet,
        ignoreTrailingSemicolon,
        allowQuotedSqlAlias
      )
        ? 1
        : 0,
    totalItems: 1,
    hasAnyAnswer,
    isComplete: hasAnyAnswer,
  };
};
